import React from 'react';
import { z } from 'zod';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { InsightDetails } from 'apps/sigma2-ui/src/pages/InsightDetails';
import { getRelatedInsights } from 'apps/sigma2-ui/src/pages/InsightsSection.utils';
import { InsightSearchParams } from 'apps/sigma2-ui/src/pages/InsightsSection.utils';
import { sortByPriority } from 'apps/sigma2-ui/src/pages/InsightsSection.utils';
import { useSearchParams } from 'react-router-dom';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { Insight } from 'libs/end-to-end/src/insights';
import { InsightsResult } from 'libs/end-to-end/src/insights';
import { KeyTakeawayHighlightType } from 'libs/end-to-end/src/key-takeaways';
import { RelatedInsights } from 'apps/sigma2-ui/src/pages/RelatedInsights';
import { AllInsights } from 'apps/sigma2-ui/src/pages/AllInsights';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';

const getInsightSearchParams = (
  searchParams: URLSearchParams,
): InsightSearchParams | null => {
  const type = KeyTakeawayHighlightType.safeParse(searchParams.get('type'));
  const dimension = z.string().safeParse(searchParams.get('dimension'));
  const treatmentLevel = z
    .string()
    .safeParse(searchParams.get('treatmentLevel'));

  const missingAnySearchParams =
    !type.success || !dimension.success || !treatmentLevel.success;

  if (missingAnySearchParams) {
    return null;
  }

  return {
    type: type.data,
    dimension: dimension.data,
    treatmentLevel: treatmentLevel.data,
  };
};

const getMatchedInsight = ({
  insights,
  insightSearchParams,
}: {
  insights: Insight[];
  insightSearchParams: InsightSearchParams;
}): Insight | null => {
  if (!insightSearchParams) {
    return null;
  }

  const matchingInsight = insights.find((insight) => {
    return (
      insight.type === insightSearchParams.type &&
      insight.dimension === insightSearchParams.dimension &&
      insight.treatmentLevel === insightSearchParams.treatmentLevel
    );
  });

  return matchingInsight ?? null;
};

const getInsightsForFocusArea = ({
  insightsResult,
  focusArea,
}: {
  insightsResult: InsightsResult;
  focusArea: FocusArea;
}): Insight[] => {
  if (focusArea in insightsResult) {
    return insightsResult[focusArea] || [];
  }

  return [];
};

export const InsightsSection: React.FC<{
  focusArea: MyFocusAreaDefinition;
}> = ({ focusArea }) => {
  const [searchParams] = useSearchParams();
  const { myObservation, selectedOrganizationId } = useObservation();

  const insightsResult = myObservation?.insights;
  const insights = insightsResult
    ? getInsightsForFocusArea({
        insightsResult,
        focusArea: focusArea.focusArea,
      })
    : [];
  const insightSearchParams = getInsightSearchParams(searchParams);
  const isLoading = myObservation === null;

  useAuthenticatedPageRedirect();

  if (!myObservation) {
    return <></>;
  }

  if (insightSearchParams) {
    const insight = getMatchedInsight({ insights, insightSearchParams });

    return (
      <InsightDetails
        insight={insight}
        focusArea={focusArea}
        isLoading={isLoading}
        relatedInsightsSection={
          insight ? (
            <RelatedInsights
              focusArea={focusArea}
              insights={getRelatedInsights({
                currentInsight: insight,
                insights,
                filters: {
                  metric: insight.metric,
                  dimension: insight.dimension,
                },
                maxInsights: 2,
              })}
              isLoading={isLoading}
              calibrationFraming={myObservation.calibrationFraming}
            />
          ) : null
        }
      />
    );
  }

  const sortedInsights = sortByPriority(insights);

  return (
    <>
      {myObservation && selectedOrganizationId && (
        <AllInsights
          isLoading={isLoading}
          insights={sortedInsights}
          focusArea={focusArea}
          calibrationFraming={myObservation.calibrationFraming}
          observation={myObservation}
        />
      )}
    </>
  );
};
